import { useState, useEffect } from 'react';

interface OfflineEntry {
  id: string;
  type: 'journal' | 'mood' | 'voice';
  data: any;
  timestamp: number;
  synced: boolean;
}

interface OfflineSyncHook {
  isOnline: boolean;
  pendingSync: number;
  saveOfflineEntry: (type: 'journal' | 'mood' | 'voice', data: any) => Promise<void>;
  syncPendingEntries: () => Promise<void>;
  getOfflineEntries: () => OfflineEntry[];
  clearSyncedEntries: () => void;
}

export const useOfflineSync = (): OfflineSyncHook => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      syncPendingEntries();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check for pending entries on mount
    updatePendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const updatePendingCount = () => {
    const entries = getOfflineEntries();
    setPendingSync(entries.filter(entry => !entry.synced).length);
  };

  const saveOfflineEntry = async (
    type: 'journal' | 'mood' | 'voice', 
    data: any
  ): Promise<void> => {
    const entry: OfflineEntry = {
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: Date.now(),
      synced: false
    };

    try {
      // Save to localStorage (in production, would use IndexedDB)
      const existingEntries = getOfflineEntries();
      const updatedEntries = [...existingEntries, entry];
      localStorage.setItem('mira_offline_entries', JSON.stringify(updatedEntries));
      
      updatePendingCount();

      // Register background sync if available
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register(`${type}-sync`);
      }
    } catch (error) {
      console.error('Failed to save offline entry:', error);
      throw error;
    }
  };

  const syncPendingEntries = async (): Promise<void> => {
    if (!isOnline) {
      console.warn('Cannot sync while offline');
      return;
    }

    const entries = getOfflineEntries();
    const pendingEntries = entries.filter(entry => !entry.synced);

    for (const entry of pendingEntries) {
      try {
        await syncEntryToServer(entry);
        markEntryAsSynced(entry.id);
      } catch (error) {
        console.error('Failed to sync entry:', entry.id, error);
        // Continue with other entries
      }
    }

    updatePendingCount();
  };

  const syncEntryToServer = async (entry: OfflineEntry): Promise<void> => {
    // Simulate API call (in production, would make actual HTTP requests)
    await new Promise(resolve => setTimeout(resolve, 500));
    
    switch (entry.type) {
      case 'journal':
        // Sync journal entry
        console.log('Syncing journal entry:', entry.data);
        break;
      case 'mood':
        // Sync mood data
        console.log('Syncing mood data:', entry.data);
        break;
      case 'voice':
        // Sync voice recording
        console.log('Syncing voice recording:', entry.data);
        break;
    }
  };

  const markEntryAsSynced = (entryId: string) => {
    const entries = getOfflineEntries();
    const updatedEntries = entries.map(entry => 
      entry.id === entryId ? { ...entry, synced: true } : entry
    );
    localStorage.setItem('mira_offline_entries', JSON.stringify(updatedEntries));
  };

  const getOfflineEntries = (): OfflineEntry[] => {
    try {
      const stored = localStorage.getItem('mira_offline_entries');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to get offline entries:', error);
      return [];
    }
  };

  const clearSyncedEntries = () => {
    const entries = getOfflineEntries();
    const unsyncedEntries = entries.filter(entry => !entry.synced);
    localStorage.setItem('mira_offline_entries', JSON.stringify(unsyncedEntries));
    updatePendingCount();
  };

  return {
    isOnline,
    pendingSync,
    saveOfflineEntry,
    syncPendingEntries,
    getOfflineEntries,
    clearSyncedEntries
  };
};